/**
 * Sothiares Custom JavaScript - BOGO System
 * ===========================================
 * Buy 2 Get 1 Free functionality adapted for the Atelier theme.
 * No Bootstrap dependency — uses native <dialog> for modals.
 *
 * IMPORTANT: This code adds the free item to the cart with a _bogo_item
 * property. For the item to actually be free, the store owner must create
 * a Shopify automatic discount (or Shopify Script/Function) that reads
 * this property and sets the price to $0. Without that server-side discount,
 * the customer will see "FREE" in the UI but be charged full price.
 *
 * Features:
 * - BOGOManager: main controller (progress bar, cart tracking, modal)
 * - Listens to Atelier's 'cart:update' events + polls cart API
 * - Integrates with SlideCart third-party app
 * - Schema-driven threshold and product selection
 * - Guards against adding multiple free items
 *
 * Author: Custom development team
 * Created: 2026-04-02
 * Fixed: 2026-04-02 — Audit fixes for event loops, dialog positioning,
 *        backdrop close, multi-add guard, event dispatch safety
 * Migrated from: PupRing BOGO system (fully rewritten for Atelier)
 */

(function () {
  'use strict';

  /**
   * BOGOManager — singleton controller for Buy 2 Get 1 Free
   */
  class BOGOManager {
    constructor() {
      if (window._bogoManager) return window._bogoManager;
      window._bogoManager = this;

      this.threshold = 2; // Default: 2 items needed for free product
      this.currentStep = 1;
      this.eligibleCount = 0;
      this.cartData = null;
      this.modalShown = false;
      this.debounceTimer = null;
      this._isUpdating = false; // Guard against re-entrant updates

      this.init();
    }

    static getInstance() {
      if (!window._bogoManager) {
        new BOGOManager();
      }
      return window._bogoManager;
    }

    // ---- Initialization ----

    init() {
      // Read threshold from the offer bar section if present
      var offerBar = document.querySelector('[data-bogo-offer-bar]');
      if (offerBar) {
        var t = parseInt(offerBar.dataset.bogoThreshold, 10);
        if (t > 0) this.threshold = t;
      }

      // Listen for Atelier theme cart events (bubbles up to document)
      document.addEventListener('cart:update', this._onCartEvent.bind(this));

      // Listen for Shopify section editor events
      document.addEventListener('shopify:section:load', function (e) {
        if (e.target.querySelector('[data-bogo-offer-bar]')) this.reinit();
      }.bind(this));

      // Bind modal close buttons and backdrop
      this.bindModalEvents();

      // Initial cart fetch
      this.fetchCartAndUpdate();

      // Also listen for SlideCart updates (polls after add-to-cart)
      this.observeSlideCartChanges();
    }

    /**
     * Cart event handler — debounced and guarded against re-entrant calls
     */
    _onCartEvent() {
      if (this._isUpdating) return; // Prevent infinite loop
      this.debouncedCartUpdate();
    }

    reinit() {
      var offerBar = document.querySelector('[data-bogo-offer-bar]');
      if (offerBar) {
        var t = parseInt(offerBar.dataset.bogoThreshold, 10);
        if (t > 0) this.threshold = t;
      }
      this.fetchCartAndUpdate();
    }

    // ---- Cart Data ----

    async fetchCart() {
      try {
        var response = await fetch('/cart.js', {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        if (!response.ok) throw new Error('Cart fetch failed');
        this.cartData = await response.json();
        return this.cartData;
      } catch (err) {
        this.cartData = { items: [], item_count: 0, total_price: 0 };
        return this.cartData;
      }
    }

    debouncedCartUpdate() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(function () {
        this.fetchCartAndUpdate();
      }.bind(this), 300);
    }

    async fetchCartAndUpdate() {
      if (this._isUpdating) return; // Prevent re-entrant calls
      this._isUpdating = true;

      try {
        await this.fetchCart();
        this.eligibleCount = this.getEligibleItemCount();
        this.updateStep();
        this.updateProgressBarUI();
        this.checkModalTrigger();
      } finally {
        this._isUpdating = false;
      }
    }

    // ---- Eligible Item Counting ----

    getEligibleItemCount() {
      if (!this.cartData || !this.cartData.items) return 0;

      var count = 0;
      for (var i = 0; i < this.cartData.items.length; i++) {
        var item = this.cartData.items[i];
        if (!this.isExcludedItem(item)) {
          count += item.quantity;
        }
      }
      return count;
    }

    isExcludedItem(item) {
      if (!item) return true;

      var title = (item.product_title || item.title || '').toLowerCase();
      var variantTitle = (item.variant_title || '').toLowerCase();
      var properties = item.properties || {};

      // Excluded keywords
      var excluded = ['warranty', 'gift-box', 'gift box', 'engraving', 'protection'];
      for (var i = 0; i < excluded.length; i++) {
        if (title.indexOf(excluded[i]) !== -1 || variantTitle.indexOf(excluded[i]) !== -1) return true;
      }

      // Check for BOGO item flag (don't count free items toward threshold)
      if (properties._bogo_item === 'true') return true;

      // Check properties for exclusion flags
      var keys = Object.keys(properties);
      for (var j = 0; j < keys.length; j++) {
        var k = keys[j].toLowerCase();
        var v = (properties[keys[j]] || '').toString().toLowerCase();
        if (k.indexOf('warranty') !== -1 || k.indexOf('protection') !== -1 ||
            v.indexOf('warranty') !== -1 || v.indexOf('protection') !== -1) {
          return true;
        }
      }

      return false;
    }

    // ---- Step Calculation ----

    updateStep() {
      var count = this.eligibleCount;
      var hasBogo = this.hasBOGOItemInCart();

      if (hasBogo && count >= 1) {
        // Already has a BOGO item — checkout ready
        this.currentStep = 3;
      } else if (count >= this.threshold) {
        // Reached threshold — select free item
        this.currentStep = 3;
      } else if (count >= 1) {
        // Has some items but not enough
        this.currentStep = 2;
      } else {
        // No eligible items
        this.currentStep = 1;
      }
    }

    hasBOGOItemInCart() {
      if (!this.cartData || !this.cartData.items) return false;
      for (var i = 0; i < this.cartData.items.length; i++) {
        var props = this.cartData.items[i].properties || {};
        if (props._bogo_item === 'true') return true;
      }
      return false;
    }

    // ---- Progress Bar UI ----

    updateProgressBarUI() {
      var bars = document.querySelectorAll('[data-bogo-offer-bar]');
      if (bars.length === 0) return;

      var self = this;
      bars.forEach(function (bar) {
        var steps = bar.querySelectorAll('[data-bogo-step]');
        var fill = bar.querySelector('[data-bogo-progress-fill]');

        steps.forEach(function (stepEl) {
          var stepNum = parseInt(stepEl.dataset.bogoStep, 10);
          stepEl.classList.remove('bogo-step--active', 'bogo-step--completed');

          if (stepNum < self.currentStep) {
            stepEl.classList.add('bogo-step--completed');
          } else if (stepNum === self.currentStep) {
            stepEl.classList.add('bogo-step--active');
          }
        });

        if (fill) {
          var percentage = ((self.currentStep - 1) / 2) * 100;
          fill.style.width = percentage + '%';
        }
      });
    }

    // ---- Modal ----

    bindModalEvents() {
      var self = this;
      var modal = document.getElementById('bogo-modal');

      if (!modal) return;

      // Close buttons (X and "no thanks")
      var closeButtons = document.querySelectorAll('[data-bogo-modal-close]');
      closeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.closeModal();
        });
      });

      // Close on backdrop click (click on dialog element itself, not its children)
      modal.addEventListener('click', function (e) {
        if (e.target === modal) {
          self.closeModal();
        }
      });

      // Handle Escape key via cancel event
      modal.addEventListener('cancel', function (e) {
        self.modalShown = false;
      });

      // Select product buttons (delegated)
      document.addEventListener('click', function (e) {
        var selectBtn = e.target.closest('[data-bogo-select]');
        if (selectBtn) {
          e.preventDefault();
          self.handleProductSelect(selectBtn);
        }
      });
    }

    checkModalTrigger() {
      // Show modal when customer reaches threshold and hasn't seen it this session
      // and doesn't already have a BOGO item in cart
      if (this.eligibleCount >= this.threshold &&
          !this.hasBOGOItemInCart() &&
          !sessionStorage.getItem('bogoModalShown')) {
        var self = this;
        setTimeout(function () {
          self.showModal();
        }, 500);
      }
    }

    showModal() {
      var modal = document.getElementById('bogo-modal');
      if (!modal || this.modalShown) return;

      if (typeof modal.showModal === 'function') {
        modal.showModal();
        this.modalShown = true;
        sessionStorage.setItem('bogoModalShown', 'true');
      }
    }

    closeModal() {
      var modal = document.getElementById('bogo-modal');
      if (!modal) return;

      if (typeof modal.close === 'function' && modal.open) {
        modal.close();
      }
      this.modalShown = false;
    }

    clearModalSession() {
      sessionStorage.removeItem('bogoModalShown');
      this.modalShown = false;
    }

    // ---- Product Selection ----

    async handleProductSelect(button) {
      // Guard: prevent adding multiple free items
      if (this.hasBOGOItemInCart()) {
        button.textContent = 'Already claimed';
        button.disabled = true;
        return;
      }

      var variantId = button.dataset.variantId;
      if (!variantId) return;

      // Disable ALL select buttons to prevent double-click
      var allButtons = document.querySelectorAll('[data-bogo-select]');
      allButtons.forEach(function (btn) { btn.disabled = true; });

      var originalText = button.textContent;
      button.textContent = 'Adding...';

      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              _bogo_item: 'true',
              _bogo_label: 'FREE - Buy 2 Get 1',
            },
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Close modal
        this.closeModal();

        // Refresh cart data (this does NOT dispatch events — avoids infinite loop)
        await this.fetchCartAndUpdate();

        // Try to refresh SlideCart if available
        if (window.SLIDECART_UPDATE) {
          try { window.SLIDECART_UPDATE(); } catch (e) { /* ignore */ }
        }

        // Show success feedback
        button.textContent = 'Added!';

      } catch (err) {
        button.textContent = 'Error - Try Again';
        // Re-enable buttons on error
        allButtons.forEach(function (btn) { btn.disabled = false; });

        var self = this;
        setTimeout(function () {
          button.textContent = originalText;
        }, 2000);
      }
    }

    // ---- SlideCart Integration ----

    observeSlideCartChanges() {
      var self = this;

      // SlideCart may load asynchronously. Try immediately and also watch for it.
      function tryObserve() {
        var slideCartRoot = document.getElementById('slidecart');
        if (!slideCartRoot) return false;

        var observer = new MutationObserver(function () {
          self.debouncedCartUpdate();
        });

        observer.observe(slideCartRoot, {
          childList: true,
          subtree: true,
        });
        return true;
      }

      // Try now
      if (!tryObserve()) {
        // Watch for SlideCart being added to DOM later
        var bodyObserver = new MutationObserver(function (mutations, obs) {
          if (tryObserve()) {
            obs.disconnect(); // Found it, stop watching
          }
        });

        bodyObserver.observe(document.body, {
          childList: true,
          subtree: false,
        });

        // Give up after 10 seconds
        setTimeout(function () {
          bodyObserver.disconnect();
        }, 10000);
      }
    }

    // ---- Public API ----

    refresh() {
      this.fetchCartAndUpdate();
    }

    getStep() {
      return this.currentStep;
    }

    getEligibleCount() {
      return this.eligibleCount;
    }
  }

  // ---- Initialize ----

  function initBOGO() {
    BOGOManager.getInstance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBOGO);
  } else {
    initBOGO();
  }

  // Export for debugging and external access
  window.BOGOManager = BOGOManager;
  window.clearBOGOModal = function () {
    var mgr = BOGOManager.getInstance();
    mgr.clearModalSession();
  };
})();
